<template>
  <section v-if="contextData">
    <div class="container-wrap py-9 tablet:py-12">
      <h2 class="text-solution-h2 mb-6 tablet:line-clamp-2">
        {{ contextData.title }}
      </h2>

      <nav v-if="contextTabs.length > 1">
        <ul
          class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:flex-wrap desktop:overflow-x-hidden"
        >
          <li
            v-for="(item, index) in contextTabs"
            :key="hash(item)"
            class="mr-2 desktop:mb-2"
          >
            <button
              class="w-full rounded-3xl border border-solid border-gray-400 px-4 py-1.5 hover:bg-blue-400 hover:text-white"
              :class="
                current === index
                  ? 'bg-blue-400 text-white'
                  : 'bg-white text-blue-400'
              "
              :title="item"
              @click="current = index"
            >
              <span class="truncate text-left text-sm font-bold">
                {{ item }}
              </span>
            </button>
          </li>
        </ul>
      </nav>

      <ul v-if="isMobile">
        <li
          v-for="item in currentItems"
          :key="hash(item)"
          class="mb-3 last:mb-0"
        >
          <SuiteResourcesMobileCard
            :item-data="item"
            :url-connector="urlConnector"
          />
        </li>
      </ul>

      <CarouselProgressbar
        v-else
        :carousel-list="currentItems"
        :slides-per-view="slidesPerView"
      >
        <template #card="{ item }">
          <SuiteResourcesCard :item-data="item" :url-connector="urlConnector" />
        </template>
      </CarouselProgressbar>
    </div>
  </section>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    caption?: string;
    name: string;
    link: string;
    thumbnail: string;
    wistiaCode?: string;
    trackingCode?: string;
  }

  interface IResourcesTab {
    tabName: string;
    items: IItem[];
  }

  interface IResourcesData {
    title: string;
    tabs: IResourcesTab[];
  }

  interface IResources {
    component: 'resources';
    meta: {
      navigable?: string;
    };
    data: IResourcesData;
  }

  type IProps = ISuiteProps<IResources>;

  const props = defineProps<IProps>();

  const { isMobile, isDesktop } = useScreen();

  const current = ref<number>(0);

  const contextData = computed((): IResourcesData => props.suiteData.data);

  const contextTabs = computed((): string[] => {
    return contextData.value.tabs.map((item: IResourcesTab) => item.tabName);
  });

  const currentItems = computed((): IItem[] => {
    return contextData.value.tabs[current.value]?.items || [];
  });

  const slidesPerView = computed((): number => {
    if (isDesktop.value) return 3;
    return 2;
  });

  export type { IItem, IResources, IResourcesData, IResourcesTab };
</script>
